/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-get, ember/require-computed-property-dependencies, ember/require-tagless-components, no-prototype-builtins, no-restricted-properties, warp-drive/no-legacy-request-patterns */
// Extend From
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
// Ember Imports
import EmberObject, { action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { alias } from '@ember/object/computed';
// External imports
import moment from 'moment';
// Common Imports
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import SchoolStatus from '@linc-technologies/linced-common/enums/school-status';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import Staff from '@linc-technologies/linced-common/enums/roles/staff';
import { defaultTinyMCEConfig } from '@linc-technologies/linced-common/utils/tinymce';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';
// App Imports
import {
  ApprovalReason,
  SuspensionCategory,
  SuspensionReason,
  SuspensionState,
  SuspensionTabs,
} from '@linc-technologies/linced-common/enums/suspensions';
import {
  calculateSuspensionLimitForLearner,
  calculateTotalSuspensionLimitForLearner,
  getFormalCautionToSuspend,
  getNoticeOfExtensionOfSuspension,
  getNoticeOfSuspension,
} from 'linced-app/utils/suspensions';

export default class SuspensionManager extends Component {
  @action
  setApprovalObtained(value) {
    this.approvalObtained = value;
  }

  @action
  setConsultationObtained(value) {
    this.consultationObtained = value;
  }

  @action
  setExtensionDuration(value) {
    this.extensionDuration = value;
  }

  @action
  setExtensionReason(value) {
    this.extensionReason = value;
  }

  @action
  setSuspensionDuration(value) {
    this.suspensionDuration = value;
  }

  SchoolStatus = SchoolStatus;
  SuspensionCategory = SuspensionCategory;
  SuspensionState = SuspensionState;
  SuspensionReason = SuspensionReason;
  SuspensionTabs = SuspensionTabs;

  // Services
  @service
  behaviourDates;

  @service
  countdownButton;

  @service
  dates;

  @service
  options;

  @service
  permissions;

  @service
  roles;

  @service
  session;

  @service
  store;

  @service
  suspensions;

  @service
  terms;

  @service
  toaster;

  @service
  uppy;

  editorOptions = Object.freeze(defaultTinyMCEConfig());

  @alias('session.school.jurisdiction')
  jurisdiction;

  close = null;
  dialogOpen = null;
  suspension = null;
  learner = null;
  loaded = false;

  // Modal options
  @tracked mode = 'action';

  @tracked step = 'confirm';

  // Params
  @tracked
  editorProperties = null;

  @tracked suspensionDuration = 0;
  @tracked
  previousExpulsions = [];
  @tracked
  previousSuspensions = [];
  suspensionPost = null;
  @tracked extensionDuration = 0;
  @tracked
  extensionReason = '';
  @tracked approvalObtained = false;
  @tracked consultationObtained = false;
  @tracked
  approvalDate = '';
  @tracked
  approvalNotes = '';
  @tracked principal = null;
  suspensionPage = null;
  suspensionLettersPage = null;
  @tracked
  lettersContent = '';

  // Options
  @optionAlias('nswDOE:delInfo:name')
  delName;

  @optionAlias('hero:behaviours:doeContact')
  doePhoneNumber;
  @optionAlias('hero:behaviours:suspensionDurationUpTo')
  suspensionDurationUpTo;

  behaviourDetailsConfig = Object.freeze(
    Object.assign(
      {
        placeholder:
          'Detail the negative behaviours that led to this suspension being required. Be sure to include details of all past behaviours, not just the one the suspension was triggered from. For the best results, include each incident and their relevant details on their own line.',
        height: 300,
      },
      defaultTinyMCEConfig(),
    ),
  );

  behaviourExpectationsConfig = Object.freeze(
    Object.assign(
      {
        placeholder:
          'Outline the behaviour that is expected from this student moving forward',
        height: 300,
      },
      defaultTinyMCEConfig(),
    ),
  );

  extensionReasonConfig = Object.freeze(
    Object.assign(
      {
        placeholder:
          'Add any details (for internal use only) as to why this extension was necessary',
        height: 200,
      },
      defaultTinyMCEConfig(),
    ),
  );

  letterConfig = Object.freeze(
    Object.assign(
      {
        height: 500,
      },
      defaultTinyMCEConfig(),
    ),
  );

  @tracked
  saveInProgress;

  @tracked
  teachers;

  @computed('session.{roles.[],scopes.[]}')
  get canEditReadonly() {
    return (
      this.roles.has(Staff.PRINCIPAL) ||
      this.permissions.meet(Scopes.LINCED_ADMIN)
    );
  }

  @computed('suspension.tab', 'canEditReadonly')
  get isReadonlyMode() {
    if (this.suspension.tab === SuspensionTabs.COMPLETED_SUSPENSIONS) {
      return !this.canEditReadonly;
    }
    if (this.suspension.tab === SuspensionTabs.FORMAL_CAUTIONS) {
      return !this.canEditReadonly;
    }
    return false;
  }

  @computed(
    'mode',
    'editorProperties.startDate',
    'suspension.{endDate,startDate}',
  )
  get startDate() {
    switch (this.mode) {
      case 'action':
        return this.editorProperties.startDate;
      case 'extend':
        return this.suspension.endDate;
      default:
        return this.suspension.startDate;
    }
  }

  @computed('mode', 'nextSchoolDay', 'suspension.nextSchoolDay')
  get returnDate() {
    return this.mode === 'action'
      ? this.nextSchoolDay
      : this.suspension.nextSchoolDay;
  }

  @computed('endDate.date')
  get nextSchoolDay() {
    let endDate = this.get('endDate.date');
    if (endDate === '') {
      return '';
    }
    return this.behaviourDates.getNextSchoolDay(
      moment(endDate, 'YYYY-MM-DD'),
      1,
    );
  }

  @computed(
    'extensionDuration',
    'mode',
    'startDate',
    'suspensionDuration',
    'suspension.endDate',
  )
  get endDate() {
    if (this.mode === 'edit') {
      return {
        date: this.get('suspension.endDate'),
        error: '',
      };
    }

    let date = '';
    let error = '';

    let duration =
      this.mode === 'extend' ? this.extensionDuration : this.suspensionDuration;
    let startDate = moment(this.startDate, 'YYYY-MM-DD');
    // Extensions start counting on the day after the start date (the return date)
    if (this.mode === 'extend') {
      startDate.add(1, 'day');
    }

    date = this.behaviourDates.getLastValidSchoolDay(startDate, duration);

    if (isEmpty(date)) {
      error =
        'Error calculating end date. Please ensure school dates have been configured for the intended duration.';
    }

    return { date, error };
  }

  @computed(
    'mode',
    'editorProperties.category',
    'suspension.category',
    'currentlySuspendedDays.length',
    'learner.numericCurrentYearLevel',
    'suspensionDuration',
    'extensionDuration',
  )
  get approvalStatus() {
    let status = {
      required: false,
      messages: [],
      reasons: [],
    };
    const props =
      this.mode === 'action' ? this.editorProperties : this.suspension;
    if (
      isEmpty(props) ||
      props.category === SuspensionCategory.FORMAL_CAUTION
    ) {
      return status;
    }
    let duration =
      this.mode === 'extend' ? this.extensionDuration : this.suspensionDuration;
    let existingSuspendedDays = this.currentlySuspendedDays.length;
    let totalLimit = calculateTotalSuspensionLimitForLearner(
      this.jurisdiction,
      this.get('learner.numericCurrentYearLevel'),
    );
    if (totalLimit && existingSuspendedDays + parseInt(duration) > totalLimit) {
      status.required = true;
      status.messages.push(
        `${this.get(
          'learner.yearLevelText',
        )} students cannot be suspended for more than ${totalLimit} days per year without DEL approval`,
      );
      status.reasons.push(
        ApprovalReason.EXCEEDS_SUSPENDED_DAYS_LIMIT_FOR_A_CALENDAR_YEAR,
      );
    }
    return status;
  }

  @computed('startDate', 'endDate.date', 'suspensionHasBeenExtended')
  get consultationRequired() {
    let startDate = this.startDate;
    let endDate = this.get('endDate.date');

    switch (this.mode) {
      case 'action':
        // HLR10 & HLR21 - Formal Cautions and Suspensions that span calendar years
        // require DEL consultation
        if (!isEmpty(startDate) && !isEmpty(endDate)) {
          return this.behaviourDates.validateSpansCalendarYears(
            startDate,
            endDate,
          );
        }
        break;
      case 'extend':
        // HLR17 & HLR18 - Extensions past the first 5 days require consultation
        // from the DEL
        if (this.suspensionHasBeenExtended) {
          return true;
        }
    }
    return false;
  }

  @computed('previousSuspensions.[]')
  get filteredPreviousSuspensions() {
    return this.previousSuspensions.filter((suspension) => {
      return suspension.category === SuspensionCategory.SUSPENSION;
    });
  }

  @computed('filteredPreviousSuspensions.@each.state')
  get activeSuspension() {
    return this.filteredPreviousSuspensions.some((suspension) => {
      return (
        suspension.state === SuspensionState.IN_PROGRESS ||
        suspension.state === SuspensionState.PENDING
      );
    });
  }

  @computed('suspension.extensions.[]')
  get suspensionHasBeenExtended() {
    return (
      this.get('suspension.extensions') &&
      this.get('suspension.extensions').length > 0
    );
  }

  @computed('startDate', 'returnDate', 'suspension.consultationDate')
  get caregiverMeetingDateWarning() {
    let returnDate = this.returnDate;
    let startDate = this.startDate;
    let meetingDate = this.suspension.consultationDate;

    if (isEmpty(meetingDate) || isEmpty(startDate) || isEmpty(returnDate)) {
      return '';
    }

    // must be a school day
    if (!this.behaviourDates.validateIsSchoolDayOrEmpty(meetingDate)) {
      return 'Meeting dates must fall on school days';
    }

    // reassign to moments for comparison
    meetingDate = moment(meetingDate, 'YYYY-MM-DD');
    startDate = moment(startDate, 'YYYY-MM-DD');
    returnDate = moment(returnDate, 'YYYY-MM-DD');

    // review date must be between start date and end date
    return meetingDate.isSameOrAfter(startDate, 'day') &&
      meetingDate.isSameOrBefore(returnDate, 'day')
      ? ''
      : 'Meeting date must be within the suspension timeframe, or on the first day back';
  }

  @computed('suspension.reviewDate')
  get reviewMeetingDateWarning() {
    return this.behaviourDates.validateIsSchoolDayOrEmpty(
      this.suspension.reviewDate,
    )
      ? ''
      : 'Warning - date entered is not a school day';
  }

  @computed('activeSuspension', 'editorProperties.category')
  get suspensionError() {
    if (this.mode !== 'action') {
      return {
        status: false,
        message: '',
      };
    }
    if (this.activeSuspension) {
      if (this.editorProperties.category >= SuspensionCategory.ACTUAL_HARM) {
        return {
          status: true,
          message: 'This student already has an existing suspension',
        };
      }
    }
    return {
      status: false,
      message: '',
    };
  }

  @computed('suspension.reason')
  get writtenReasonRequired() {
    let reason = this.get('suspension.reason');
    return (
      reason === SuspensionReason.OTHER___BEHAVIOUR_OF_CONCERN ||
      reason === SuspensionReason.OTHER___SERIOUS_BEHAVIOUR_OF_CONCERN
    );
  }

  @computed(
    'startDate',
    'editorProperties.category',
    'currentlySuspendedDays',
    'intendedSuspendedDays',
  )
  get startDateError() {
    if (this.mode !== 'action') {
      return '';
    }
    const startDate = this.startDate;
    if (isEmpty(this.startDate)) {
      return 'Start date is required';
    }

    if (!this.behaviourDates.validateIsSchoolDayOrEmpty(startDate)) {
      return 'Start date must be a school day';
    }
    if (this.editorProperties.category === SuspensionCategory.FORMAL_CAUTION) {
      return '';
    }
    let currentlySuspendedDays = this.currentlySuspendedDays;
    let intendedSuspendedDays = new Set(this.intendedSuspendedDays);

    if (currentlySuspendedDays.some((day) => intendedSuspendedDays.has(day))) {
      return 'Suspensions cannot overlap one another';
    }

    return '';
  }

  @computed('startDateError', 'endDate.error')
  get datesErrorMessage() {
    let message = '';

    if (this.endDate.error) {
      message = this.endDate.error;
    }
    if (this.startDateError) {
      message = this.startDateError;
    }

    return message;
  }

  @computed(
    'approvalObtained',
    'approvalStatus.required',
    'caregiverMeetingDateWarning',
    'consultationObtained',
    'consultationRequired',
    'datesErrorMessage',
    'extensionDuration',
    'maximumDuration',
    'mode',
    'step',
    'suspension.{writtenReason,isValid}',
    'suspensionDuration',
    'suspensionError.status',
    'writtenReasonRequired',
  )
  get isValid() {
    if (this.step === 'confirm') {
      return true;
    }
    switch (this.mode) {
      case 'extend':
        if (this.extensionDuration < 1 || this.extensionDuration > 5) {
          return false;
        }
        break;
      case 'edit':
        if (this.writtenReasonRequired) {
          if (isEmpty(this.suspension.writtenReason)) {
            return false;
          }
        }
        return this.suspension.isValid;
      default:
        break;
    }
    if (this.mode === 'extend') {
      if (this.consultationRequired) {
        if (!this.consultationObtained) {
          return false;
        }
      }
      if (this.approvalStatus.required) {
        if (!this.approvalObtained) {
          return false;
        }
      }
    }
    if (this.step === 'details') {
      if (!isEmpty(this.datesErrorMessage)) {
        return false;
      }
      if (this.suspensionError.status) {
        return false;
      }
      if (this.suspensionDuration > this.maximumDuration.number) {
        return false;
      }
      if (this.caregiverMeetingDateWarning) {
        if (this.mode !== 'extend') {
          return false;
        }
      }
    }
    return this.suspension.isValid;
  }

  @computed('mode', 'editorProperties.category', 'suspension.category')
  get letterTemplate() {
    if (this.mode === 'extend') {
      return getNoticeOfExtensionOfSuspension();
    }
    let category =
      this.mode === 'edit'
        ? this.suspension.category
        : this.editorProperties.category;
    if (category === SuspensionCategory.FORMAL_CAUTION) {
      return getFormalCautionToSuspend();
    }
    return getNoticeOfSuspension(
      this.suspensionDurationUpTo,
      this.isPortersCreek,
    );
  }

  @computed(
    'editorProperties.category',
    'formalCautionMax',
    'learner.numericCurrentYearLevel',
  )
  get maximumDuration() {
    if (
      this.get('editorProperties.category') > SuspensionCategory.FORMAL_CAUTION
    ) {
      let limit = calculateSuspensionLimitForLearner(
        this.jurisdiction,
        this.get('learner.numericCurrentYearLevel'),
      );
      return {
        number: limit,
        message: `${this.get(
          'learner.yearLevelText',
        )} students may only be suspended for up to ${limit} school days at a time`,
      };
    }

    let number = 50;
    if (this.formalCautionMax) {
      number = this.formalCautionMax;
    }
    return {
      number,
      message: 'Formal cautions may not exceed 50 days',
    };
  }

  get principalDetails() {
    // No principal found, just return an empty object for now
    if (isEmpty(this.principal)) {
      return EmberObject.create({});
    }
    return {
      name: this.principal.fullName,
      signature: this.principal.firstName[0] + this.principal.lastName[0],
    };
  }

  @computed(
    'suspension.{reviewDate,startDate,endDate}',
    'editorProperties.startDate',
    'endDate.date',
    'mode',
  )
  get reviewDateIsValid() {
    let endDate = this.endDate.date;
    let startDate = this.startDate;
    let reviewDate = this.suspension.reviewDate;

    if (
      isEmpty(this.suspension.reviewDate) ||
      isEmpty(startDate) ||
      isEmpty(endDate)
    ) {
      return true;
    }

    // must be a school day
    if (!this.behaviourDates.validateIsSchoolDayOrEmpty(reviewDate)) {
      return false;
    }

    // reassign to moments for comparison
    reviewDate = moment(reviewDate, 'YYYY-MM-DD');
    startDate = moment(startDate, 'YYYY-MM-DD');
    endDate = moment(endDate, 'YYYY-MM-DD');

    // review date must be between start date and end date
    return (
      reviewDate.isSameOrAfter(startDate, 'day') &&
      reviewDate.isBefore(endDate, 'day')
    );
  }

  @computed(
    'filteredPreviousSuspensions.@each.{startDate,endDate,earlyReturnDate}',
  )
  get currentlySuspendedDays() {
    if (this.filteredPreviousSuspensions.length === 0) {
      return [];
    }
    let suspendedDays = [];
    this.filteredPreviousSuspensions.forEach((suspension) => {
      let currentDay = moment(suspension.startDate, 'YYYY-MM-DD');
      while (currentDay.isSameOrBefore(suspension.displayEndDate, 'day')) {
        if (
          this.behaviourDates.validateIsSchoolDay(
            currentDay.format('YYYY-MM-DD'),
          )
        ) {
          suspendedDays.push(currentDay.format('YYYY-MM-DD'));
        }
        currentDay.add(1, 'days');
      }
    });
    return suspendedDays;
  }

  @computed('editorProperties.startDate', 'suspension.startDate', 'endDate')
  get intendedSuspendedDays() {
    let startDate = this.startDate;
    let endDate = this.endDate.date;
    let days = [];

    if (!startDate || !endDate) {
      return days;
    }

    days = this.behaviourDates.calculateSchoolDaysInRange(
      startDate,
      endDate,
      true,
    );

    return days;
  }

  // Hacky id check for porters creek. Dynamic editable letter templates will replace this when they arrive. Temporary
  @computed('session.school.id')
  get isPortersCreek() {
    return (
      this.get('session.school.id') === '3d410502-b223-458e-ac0a-c6fa26ca5f8c'
    );
  }

  // deprecated at this time - all DEL emails are handled through the DOE directly
  /*  checkDelInformStatus() {
    let suspension = this.get('suspension');
    let learner = suspension.get('learner');

    // All kindergarten suspensions send a notification to the DEL
    if (learner.get('numericCurrentYearLevel') < 1) {
      suspension.set(
        'notificationReason',
        NotificationReason.KINDERGARTEN_SUSPENSION
      );
      return;
    }
    // Suspensions/formal cautions that required consultation send a notification
    // to the DEL
    if (this.consultationRequired) {
      suspension.set(
        'notificationReason',
        NotificationReason.SPANS_CALENDAR_YEARS
      );
      return;
    }

    if (this.mode === 'action') {
      suspension.set('notificationReason', NotificationReason.GENERIC);
    }
  },*/

  init() {
    super.init(...arguments);
  }

  async fetchPreviousSuspensions() {
    let res = await this.store.query('attendance/suspension', {
      schoolId: this.get('session.school.id'),
      learnerId: this.get('suspension.learner_id'),
      dateFrom: moment().startOf('year').format('YYYY-MM-DD'),
      dateTo: moment().endOf('year').format('YYYY-MM-DD'),
    });
    this.previousSuspensions = res;
  }

  async fetchPreviousExpulsions() {
    let res = await this.store.query('attendance/expulsion', {
      schoolId: this.get('session.school.id'),
      learnerId: this.get('suspension.learner_id'),
      dateFrom: moment().startOf('year').format('YYYY-MM-DD'),
      dateTo: moment().endOf('year').format('YYYY-MM-DD'),
    });
    this.previousExpulsions = res;
  }

  initialiseValues() {
    switch (this.mode) {
      case 'action':
        this.editorProperties = {
          category: SuspensionCategory.FORMAL_CAUTION,
          reason: SuspensionReason.UNSPECIFIED,
          startDate: moment().format('YYYY-MM-DD'),
        };

        this.suspensionDuration = this.get('maximumDuration.number');
        this.step = 'confirm';
        this.countdownButton.initialise(3);
        break;
      case 'edit':
        this.step = 'details';
        break;
      case 'extend':
        this.extensionDuration = 0;
        this.extensionReason = '';
        this.approvalObtained = false;
        this.approvalDate = '';
        this.approvalNotes = '';
        this.step = 'details';
        break;
      default:
    }

    if (this.get('suspension.learner_id')) {
      this.fetchPreviousSuspensions();
      this.fetchPreviousExpulsions();
    }
    this.teachers = this.store
      .peekAll('person')
      .filter((person) => person.type === PersonType.STAFF);
    this.suspensions.initialise(
      this.mode,
      this.principalDetails,
      this.suspension,
      this.suspensionPage,
      this.suspensionLettersPage,
    );
  }

  didReceiveAttrs() {
    super.didReceiveAttrs(...arguments);
    if (this.dialogOpen) {
      return this.initialiseValues();
    }

    // prevent memory leaking if dialog is closed
    this.countdownButton.cleanup();
  }

  generatePreviewLetters() {
    // Custom get function so we can mock suspension and extension objects for the
    // letter processer to handle
    function get(key) {
      const keys = key.split('.');
      let value = this;
      keys.forEach((k) => {
        if (value && value.hasOwnProperty(k)) {
          value = value[k];
        } else {
          value = undefined;
        }
      });
      return value;
    }

    let suspension = this.suspension;
    let extension = null;

    switch (this.mode) {
      case 'action':
        suspension = {
          learner: this.suspension.get('learner'),
          duration: this.suspensionDuration,
          startDate: this.startDate,
          endDate: this.endDate.date,
          reason: this.suspension.reason,
          writtenReason: this.suspension.writtenReason,
          nextSchoolDay: this.nextSchoolDay,
          behaviourExpectations: this.suspension.behaviourExpectations,
          behaviourDetails: this.suspension.behaviourDetails,
          caregivers: this.suspension.get('caregivers'),
          nominatedContact: this.suspension.get('nominatedContact'),
          extensions: [
            {
              dayCount: this.extensionDuration,
              reason: this.extensionReason,
            },
          ],
          get,
        };
        break;
      case 'extend':
        extension = {
          newEndDate: this.endDate.date,
          dayCount: this.extensionDuration,
          nextSchoolDay: this.nextSchoolDay,
          get,
        };
        break;
    }

    if (suspension.get('caregivers').length === 0) {
      return;
    }
    this.lettersContent = this.suspensions.generateLettersContent(
      this.letterTemplate,
      suspension,
      extension,
    );
  }

  processExtension(suspension) {
    if (suspension.earlyReturnDate) {
      suspension.set('earlyReturnDate', '');
    }

    let extension = this.store.createFragment('wellbeing/extension', {
      initialEndDate: this.startDate,
      newEndDate: this.endDate.date,
      dayCount: this.extensionDuration,
      approved: this.approvalObtained,
      reason: this.extensionReason,
    });

    if (this.suspensionHasBeenExtended) {
      suspension.get('approvals').pushObject({
        reason: ApprovalReason.EXTENSION,
        approved: this.approvalObtained,
        date: this.approvalDate,
        notes: this.approvalNotes,
      });
    }
    suspension.get('extensions').pushObject(extension);
    suspension.set('extended', true);
    suspension.setProperties({
      endDate: extension.newEndDate,
      duration:
        parseInt(suspension.duration) + parseInt(this.extensionDuration),
    });
  }

  async saveSuspension(suspension) {
    let suspensionPromises = [];

    if (this.mode === 'extend') {
      this.processExtension(suspension);
      await this.suspensions.sendExtensionLetter(
        suspension,
        this.lettersContent,
      );
    }

    if (this.mode === 'action') {
      suspension.setProperties({
        active: true,
        category: this.editorProperties.category,
        duration: this.suspensionDuration,
        endDate: this.endDate.date,
        needsApproval: this.approvalStatus.required,
        startDate: this.startDate,
      });

      await this.suspensions.sendSuspensionLetter(
        suspension,
        this.lettersContent,
      );

      if (this.approvalStatus.required) {
        this.approvalStatus.reasons.forEach((reason) => {
          suspension.get('approvals').pushObject({ reason });
        });
      }
    }

    let props = this.editorProperties;
    if (this.mode === 'edit' || !props) {
      props = suspension;
    }

    suspensionPromises.push(
      ...this.suspensions.processSuspensionDocuments(suspension, props),
    );

    return suspensionPromises;
  }

  @action
  setCurrentSuspensionDuration(duration) {
    this.suspension.set('duration', duration);

    let startDate = moment(this.suspension.get('startDate'), 'YYYY-MM-DD');
    let endDate = this.behaviourDates.getLastValidSchoolDay(
      startDate,
      duration,
    );

    this.suspension.set('endDate', endDate);
  }

  @action
  setCurrentSuspensionStartDate(startDate) {
    this.suspension.set('startDate', startDate);

    let endDate = this.behaviourDates.getLastValidSchoolDay(
      startDate,
      this.suspension.get('duration'),
    );

    this.suspension.set('endDate', endDate);
  }

  @action
  previousStep() {
    return (this.step = 'details');
  }

  @action
  nextStep() {
    if (this.step === 'confirm') {
      return (this.step = 'details');
    }
    this.generatePreviewLetters();
    return (this.step = 'review');
  }

  @action
  async addCaregiver(person) {
    // resolve the async hasMany relationship upfront.
    (await this.suspension.caregivers).push(person);
  }

  @action
  async removeCaregiver(person) {
    // resolve the async hasMany relationship upfront.
    let caregivers = await this.suspension.caregivers;
    let index = caregivers.indexOf(person);
    if (index !== -1) {
      caregivers.splice(index, 1);
    }
  }

  @action
  async save() {
    this.saveInProgress = true;

    // Wellbeing events are not required to send DEL notifications at this time
    // this.checkDelInformStatus();
    let suspension = this.suspension;
    let suspensionPromises = await this.saveSuspension(suspension);

    return Promise.all(suspensionPromises)
      .then(() =>
        suspension.save().catch(() => {
          this.toaster.error('Error saving suspension');
        }),
      )
      .then(() => {
        this.toaster.success(
          `Suspension ${
            this.mode === 'extend' ? 'extended' : 'saved'
          } successfully`,
        );
      })
      .catch(() => {
        this.toaster.error(
          'An unexpected error has occurred. Please try again',
        );
      })
      .finally(() => {
        this.saveInProgress = false;
        this.close();
      });
  }

  @action
  setCategory(category) {
    this.set('editorProperties.category', category.value);
    this.suspensionDuration = this.get('maximumDuration.number');
  }

  @action
  addAttachments() {
    this.uppy.addFiles();
  }

  @action
  removeAttachment(attachment) {
    this.suspensions.removeAttachment(attachment);
  }

  @action
  removeNominatedContact() {
    this.suspension.set('nominatedContact', null);
  }

  @action
  toggleImmediateThreat() {
    this.suspension.toggleProperty('immediateThreat');
  }

  @action
  closeDialog() {
    this.close();
  }

  @action
  noOp() {
    //do nothing
  }
}
